import React, { useEffect, useMemo, useRef, useState } from 'react';
import SafeIcon from '../../common/SafeIcon';

const getNodeText = (node) => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(getNodeText).join(' ');
  if (React.isValidElement(node)) {
    const labelled = node.props?.label ? `${node.props.label} ` : '';
    return `${labelled}${getNodeText(node.props?.children)}`.trim();
  }
  return '';
};

const isDocumentsTableColumns = (columns = []) => (
  Array.isArray(columns) &&
  columns.length === 8 &&
  columns[0] === 'Document' &&
  columns[1] === 'Type' &&
  columns[2] === 'Linked To' &&
  columns[5] === 'Files' &&
  columns[7] === 'Actions'
);

const isRentScheduleTableColumns = (columns = []) => {
  if (!Array.isArray(columns) || columns.length !== 12) return false;
  const labels = columns.map(column => getNodeText(column));
  return (
    labels[0].includes('Due Date') &&
    labels[1].includes('Rent Period Covered') &&
    labels[2].includes('Property') &&
    labels[9].includes('Paid Status') &&
    labels[10].includes('Status') &&
    labels[11] === 'Actions'
  );
};

const isPropertiesTableColumns = (columns = []) => {
  if (!Array.isArray(columns) || ![11,12].includes(columns.length)) return false;
  const labels = columns.map(column => getNodeText(column));
  return (
    labels[0] === 'Property' &&
    (
      labels.includes('Anticipated Monthly Net Rent') ||
      labels.includes('Monthly Net Rent') ||
      labels.includes('Monthly Anticipated Rent')
    ) &&
    labels.includes('Monthly Installment') &&
    labels.includes('Equity') &&
    labels.includes('Net Cash Flow')
  );
};

const propertyNumericColumnLabels = new Set([
  'Market Value',
  'Acquisition Cost',
  'Loan Bal',
  'Interest Rate',
  'Monthly Installment',
  'Anticipated Monthly Net Rent',
  'Monthly Net Rent',
  'Monthly Anticipated Rent',
  'Agent Fees / Rate',
  'Equity',
  'Net Cash Flow'
]);

const formatDocumentDateAU = (value) => {
  if (!value) return '-';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

export const PageHeader = ({ title, subtitle, actionLabel, onAction, backAction, compact = false, mobileActionFullWidth = false }) => (
  <div className={`flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 ${compact ? 'mb-4 sm:mb-5' : 'mb-8'}`}>
    <div className="flex min-w-0 items-center gap-4">
      {backAction && (
        <button onClick={backAction} className="min-h-[44px] min-w-[44px] p-2 -ml-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
          <SafeIcon icon="ArrowLeft" className="text-xl" />
        </button>
      )}
      <div className="min-w-0">
        <h1 className={`${compact ? 'text-xl sm:text-2xl' : 'text-2xl'} break-words font-bold text-slate-900`}>{title}</h1>
        {subtitle && <p className={`${compact ? 'text-xs sm:text-sm mt-0.5' : 'text-sm mt-1'} break-words text-slate-500`}>{subtitle}</p>}
      </div>
    </div>
    {actionLabel && (
      <button onClick={onAction} className={`inline-flex min-h-[44px] items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm ${mobileActionFullWidth ? 'w-full sm:w-auto' : ''}`}>
        <SafeIcon icon="Plus" />
        {actionLabel}
      </button>
    )}
  </div>
);

const getDefaultKpiHelp = (label, value, supportingText) => {
  const context = supportingText ? ` Current card context: ${supportingText}.` : '';
  const result = value !== undefined && value !== null && value !== '' ? ` Displayed result: ${value}.` : '';
  const helpByLabel = {
    'Market Value': 'Market Value is the latest current market value recorded for the property. It is an input, not a derived figure. Acquisition Cost is purchase price plus the applicable recorded acquisition-cost fields; legacy aggregate costs and itemised costs are not added together twice.',
    'Portfolio Market Value': 'Portfolio Market Value is the sum of the current market values of the properties included in the selected portfolio scope. Portfolio aggregation is audited separately from individual-property calculations.',
    'Acquisition Cost': 'Acquisition Cost = Purchase Price + applicable recorded acquisition costs. Where both a legacy aggregate and itemised acquisition costs exist, the engine avoids adding both totals together and double-counting the same cost.',
    'Loan Balance': 'Current Loan Balance uses active linked loan records first. currentLoanBalance is preferred, legacy currentBalance is the compatibility fallback, and property financial details are used only when no active linked loan exists. Linked-loan and property-level balances are never added together.',
    'Total Equity': 'Equity = Current Market Value − Current Loan Balance. Individual-property debt uses the authoritative loan-source precedence, so the property-level fallback is not added on top of linked loans.',
    'LVR': 'LVR = Current Loan Balance ÷ Current Market Value × 100. A non-zero current market value is required for the ratio to be meaningful.',
    'Net Cash Flow': 'For an individual property, Net Cash Flow is Cash Flow After Finance: actual operating income − actual operating expenses = NOI, then NOI − actual financing cash outflow. Actual loan amounts deducted are the cash outflow; interest, principal and fees explain the repayment and are not subtracted a second time. Where loan-payment records are missing, the result is marked provisional rather than silently estimating principal.',
    'Cash Flow After Finance': 'Cash Flow After Finance = Net Operating Income − actual financing cash outflow. Loan-payment amount deducted is used as the cash outflow when available; interest, principal and fees are components of that repayment, not additional deductions.',
    'Cash Flow After Loan': 'Cash Flow After Finance = Net Operating Income − actual financing cash outflow. Loan-payment amount deducted is used as the cash outflow when available; interest, principal and fees are components of that repayment, not additional deductions.',
    'Occupancy': 'Occupancy = occupied rentable spaces ÷ total rentable spaces. Archived spaces are excluded from the individual-property summary.',
    'Occupancy Rate': 'Occupancy Rate = occupied rentable spaces ÷ total rentable spaces. Archived spaces are excluded from the individual-property summary.',
    'Annual Gross Rent': 'Annual Gross Rent is expected contractual rent, not proof of cash received. Active leases are authoritative; occupied-space rent is used only when there is no active lease. Rent is annualised by its recorded basis: weekly × 52, fortnightly × 26, monthly × 12, quarterly × 4, annual × 1. Unsupported/custom bases are flagged rather than assumed to be weekly.',
    'Gross Yield': 'Gross Yield = Expected Annual Gross Rent ÷ Current Market Value × 100. Expected contractual rent is deliberately kept separate from actual rent received.',
    'Operating Income': 'Actual Operating Income includes Paid operating-income transactions such as rent and reimbursements for the selected period. Expected rent schedules are not treated as money received.',
    'Operating Expenses': 'Actual Operating Expenses include Paid operating-cost transactions such as agent fees, council, water, strata, repairs, maintenance, insurance, utilities, cleaning and gardening. Financing, capital, acquisition and private items are excluded from NOI.',
    'Net Operating Income': 'Net Operating Income (NOI) = Actual Operating Income − Actual Operating Expenses. Loan principal, capital/acquisition costs, transfers and private items are not operating expenses.',
    'Net Yield': 'For the authoritative individual-property summary, Net Yield = trailing-12-month NOI ÷ Current Market Value × 100. The result is flagged as provisional if the trailing-12-month operating dataset contains review-required classifications or does not yet contain a usable Paid operating history. Portfolio net yield is audited separately.',
    'Weekly Rent': 'The frontend may use the legacy label Weekly Rent, but the amount must be interpreted with the recorded rent basis. Annualisation uses weekly × 52, fortnightly × 26, monthly × 12, quarterly × 4 or annual × 1; a non-weekly amount must not be multiplied by 52.',
    'Bond Held': 'Bond Held is the bond amount recorded for the lease. The equivalent weeks figure compares that bond amount with the lease rent amount and should be interpreted with the recorded rent basis.',
    'Tenure': 'Tenure is the elapsed time from the lease start date to today.',
    'Lease Expiry': 'Lease Expiry is the number of calendar days from today to the recorded lease end date.',
    'Unrealised Gain': 'Unrealised Gain/Loss against acquisition cost = Current Market Value − Total Acquisition Cost. It is not a realised sale profit and does not include selling costs or tax.'
  };
  const explanation = helpByLabel[label];
  return explanation ? `${explanation}${result}${context}` : '';
};

export const KPICard = ({ label, value, supportingText, status = 'neutral', helpText, compact = false }) => {
  const statusStyles = {
    neutral: 'bg-white border-slate-200',
    success: 'bg-emerald-50 border-emerald-100',
    warning: 'bg-amber-50 border-amber-100'
  };
  const resolvedHelpText = helpText || getDefaultKpiHelp(label, value, supportingText);
  return (
    <div className={`${compact ? 'p-3.5 xl:p-4' : 'p-5'} rounded-xl border shadow-sm ${statusStyles[status]}`}>
      <div className="flex items-start justify-between gap-2 mb-1">
        <h3 className={`${compact ? 'text-xs 2xl:text-[13px]' : 'text-sm'} font-medium text-slate-500`}>{label}</h3>
        {resolvedHelpText && (
          <span className="relative group flex-shrink-0">
            <button type="button" aria-label={`How ${label} is calculated`} className="w-6 h-6 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-amber-500">
              <SafeIcon icon="Info" className="text-sm" />
            </button>
            <span role="tooltip" className="pointer-events-none absolute right-0 top-8 z-50 hidden group-hover:block group-focus-within:block w-72 max-w-[80vw] rounded-lg bg-slate-900 px-3 py-2 text-left text-xs leading-5 font-normal text-white shadow-xl whitespace-normal">
              {resolvedHelpText}
            </span>
          </span>
        )}
      </div>
      <div className={`${compact ? 'text-xl 2xl:text-[22px]' : 'text-2xl'} font-bold text-slate-900 mb-1 truncate`}>{value}</div>
      {supportingText && <p className={`${compact ? 'text-[11px] leading-4' : 'text-xs'} text-slate-400 truncate`}>{supportingText}</p>}
    </div>
  );
};

export const DataTable = ({ columns, data, emptyMessage = "No records found", renderRow, onRowClick, stickyLastColumn = false, columnHelp = {} }) => {
  const documentsTable = isDocumentsTableColumns(columns);
  const rentScheduleTable = isRentScheduleTableColumns(columns);
  const propertiesTable = isPropertiesTableColumns(columns);
  const [sortConfig, setSortConfig] = useState({ index: null, direction: 'asc' });
  const [rentPageSize, setRentPageSize] = useState(50);
  const [rentPage, setRentPage] = useState(1);
  const rentScrollRef = useRef(null);

  const preparedRows = useMemo(() => (
    (data || []).map((row, originalIndex) => ({
      row,
      originalIndex,
      cells: renderRow ? renderRow(row) : Object.values(row)
    }))
  ), [data, renderRow]);

  const displayedRows = useMemo(() => {
    if ((!documentsTable && !propertiesTable) || sortConfig.index === null) return preparedRows;
    const direction = sortConfig.direction === 'desc' ? -1 : 1;
    return [...preparedRows].sort((a, b) => {
      const index = sortConfig.index;
      let aValue;
      let bValue;

      if (documentsTable && index === 3) {
        aValue = new Date(a.row?.documentDate || a.row?.uploadedDate || 0).getTime() || 0;
        bValue = new Date(b.row?.documentDate || b.row?.uploadedDate || 0).getTime() || 0;
      } else if (documentsTable && index === 5) {
        aValue = Number.parseInt(getNodeText(a.cells[index]), 10) || 0;
        bValue = Number.parseInt(getNodeText(b.cells[index]), 10) || 0;
      } else if (propertiesTable && propertyNumericColumnLabels.has(getNodeText(columns[index]))) {
        const parseNumericCell = (cells) => {
          const explicitSortValue = cells[index]?.props?.['data-sort-value'];
          if (explicitSortValue !== undefined && explicitSortValue !== null && explicitSortValue !== '') {
            const numericSortValue = Number(explicitSortValue);
            if (Number.isFinite(numericSortValue)) return numericSortValue;
          }
          const text = getNodeText(cells[index]).replace(/[^0-9.-]/g, '');
          return text === '' || text === '-' ? 0 : Number(text) || 0;
        };
        aValue = parseNumericCell(a.cells);
        bValue = parseNumericCell(b.cells);
      } else {
        aValue = getNodeText(a.cells[index]).trim();
        bValue = getNodeText(b.cells[index]).trim();
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        if (aValue === bValue) return a.originalIndex - b.originalIndex;
        return (aValue - bValue) * direction;
      }

      const compared = String(aValue).localeCompare(String(bValue), 'en-AU', { numeric: true, sensitivity: 'base' });
      return compared === 0 ? a.originalIndex - b.originalIndex : compared * direction;
    });
  }, [columns, documentsTable, preparedRows, propertiesTable, sortConfig]);

  const rentDataKey = useMemo(() => (
    rentScheduleTable ? (data || []).map(row => row?.id || '').join('|') : ''
  ), [rentScheduleTable, data]);

  const rentTotalPages = rentScheduleTable ? Math.max(1, Math.ceil(displayedRows.length / rentPageSize)) : 1;

  useEffect(() => {
    if (!rentScheduleTable) return;
    setRentPage(current => Math.min(Math.max(1, current), rentTotalPages));
  }, [rentScheduleTable, rentTotalPages]);

  useEffect(() => {
    if (!rentScheduleTable) return;
    setRentPage(1);
    if (rentScrollRef.current) rentScrollRef.current.scrollTop = 0;
  }, [rentScheduleTable, rentPageSize, rentDataKey]);

  const rowsToRender = useMemo(() => {
    if (!rentScheduleTable) return displayedRows;
    const start = (rentPage - 1) * rentPageSize;
    return displayedRows.slice(start, start + rentPageSize);
  }, [rentScheduleTable, displayedRows, rentPage, rentPageSize]);

  const goToRentPage = (nextPage) => {
    const page = Math.min(rentTotalPages, Math.max(1, Number(nextPage) || 1));
    setRentPage(page);
    requestAnimationFrame(() => {
      if (rentScrollRef.current) rentScrollRef.current.scrollTop = 0;
    });
  };

  const handleSort = (index) => {
    if ((!documentsTable && !propertiesTable) || (documentsTable && index === columns.length - 1)) return;
    setSortConfig(current => {
      if (current.index === index) {
        return { index, direction: current.direction === 'asc' ? 'desc' : 'asc' };
      }
      const defaultDirection = documentsTable && index === 3 ? 'desc' : 'asc';
      return { index, direction: defaultDirection };
    });
  };

  const resolvedColumnLabel = (column, index) => documentsTable && index === 3 ? 'Document Date' : column;
  const rentColumnWidths = ['7.5%', '9%', '10%', '8.5%', '7%', '6%', '6.5%', '7%', '7%', '10%', '7%', '14.5%'];
  const propertyColumnWidths = columns.length===12
    ? ['11.5%', '11.5%', '4.5%', '7.5%', '7.5%', '7%', '6%', '8.5%', '10.5%', '9.5%', '7%', '9%']
    : ['13%', '14%', '5%', '8%', '8%', '8%', '7%', '10%', '11%', '8%', '8%'];
  const rentStartRow = displayedRows.length > 0 ? ((rentPage - 1) * rentPageSize) + 1 : 0;
  const rentEndRow = Math.min(rentPage * rentPageSize, displayedRows.length);

  return (
    <div
      ref={rentScheduleTable ? rentScrollRef : undefined}
      className={`bg-white border border-slate-200 rounded-xl shadow-sm flex flex-col w-full ${
        documentsTable
          ? 'overflow-x-auto xl:overflow-visible'
          : rentScheduleTable
            ? 'overflow-x-hidden overflow-y-auto overscroll-contain'
            : propertiesTable
              ? 'overflow-hidden'
              : 'overflow-hidden overflow-x-auto'
      }`}
      data-documents-table={documentsTable ? 'true' : undefined}
      data-rent-schedules-table={rentScheduleTable ? 'true' : undefined}
      style={rentScheduleTable ? { maxHeight: '52dvh' } : undefined}
    >
      <table className={rentScheduleTable || propertiesTable ? 'w-full text-left border-collapse table-fixed min-w-0' : 'w-full text-left border-collapse min-w-max'}>
        {rentScheduleTable && (
          <colgroup>
            {rentColumnWidths.map((width, index) => <col key={index} style={{ width }} />)}
          </colgroup>
        )}
        {propertiesTable && (
          <colgroup>
            {propertyColumnWidths.map((width, index) => <col key={index} style={{ width }} />)}
          </colgroup>
        )}
        <thead
          className={documentsTable || rentScheduleTable ? 'sticky z-30 bg-slate-50 shadow-[0_1px_0_rgba(148,163,184,0.35)]' : ''}
          style={documentsTable ? { top: 'var(--documents-table-sticky-top, 0px)' } : rentScheduleTable ? { top: 0 } : undefined}
        >
          <tr className="bg-slate-50 border-b border-slate-200">
            {columns.map((col, i) => {
              const label = resolvedColumnLabel(col, i);
              const displayLabel = rentScheduleTable && React.isValidElement(label)
                ? React.cloneElement(label, { className: `${label.props?.className || ''} !whitespace-normal leading-tight` })
                : label;
              const sortable = (documentsTable && i !== columns.length - 1) || propertiesTable;
              const active = sortConfig.index === i;
              const dateHelp = documentsTable && i === 3 ? 'Document Date is the date recorded in the document metadata. Legacy records without a Document Date fall back to the upload date.' : undefined;
              const helpText = columnHelp[getNodeText(label)];
              const tooltipAlignment = i >= columns.length - 2
                ? 'right-0'
                : i <= 1
                  ? 'left-0'
                  : 'left-1/2 -translate-x-1/2';
              return (
                <th
                  key={i}
                  title={dateHelp}
                  className={`${rentScheduleTable ? 'px-2 py-2.5 text-[10px] xl:text-[11px] normal-case tracking-wide leading-tight align-top' : propertiesTable ? 'px-2 py-2.5 text-[9px] 2xl:text-[10px] uppercase tracking-wide leading-tight align-top whitespace-normal break-words' : 'px-6 py-3 text-xs uppercase tracking-wider'} font-semibold text-slate-600 bg-slate-50 ${stickyLastColumn && i === columns.length - 1 ? 'sticky right-0 z-40 min-w-[170px] border-l border-slate-200 shadow-[-10px_0_14px_-14px_rgba(15,23,42,0.55)]' : ''}`}
                >
                  <div className="inline-flex max-w-full items-start gap-1.5">
                    {sortable ? (
                      <button
                        type="button"
                        onClick={(event) => { event.preventDefault(); event.stopPropagation(); handleSort(i); }}
                        className={`inline-flex max-w-full items-center gap-1.5 text-left hover:text-slate-900 focus:outline-none focus:text-slate-900 ${propertiesTable ? "whitespace-normal break-words leading-tight" : "whitespace-nowrap"}`}
                        aria-label={`Sort by ${label}${active ? `, currently ${sortConfig.direction}ending` : ""}`}
                      >
                        <span className={propertiesTable ? 'whitespace-normal break-words' : ''}>{label}</span>
                        <span className={`text-[10px] leading-none ${active ? "text-amber-600" : "text-slate-300"}`} aria-hidden="true">
                          {active ? (sortConfig.direction === "asc" ? "▲" : "▼") : "↕"}
                        </span>
                      </button>
                    ) : displayLabel}
                    {helpText && (
                      <span className="relative group mt-0.5 inline-flex shrink-0">
                        <span
                          tabIndex={0}
                          aria-label={`How ${getNodeText(label)} is calculated`}
                          className="inline-flex h-4 w-4 items-center justify-center rounded-full text-slate-400 outline-none hover:bg-slate-100 hover:text-slate-700 focus:bg-slate-100 focus:text-slate-700 focus:ring-2 focus:ring-amber-500"
                        >
                          <SafeIcon icon="Info" className="text-[10px]" />
                        </span>
                        <span role="tooltip" className={`pointer-events-none absolute ${tooltipAlignment} top-5 z-50 hidden w-80 max-w-[calc(100vw-2rem)] whitespace-pre-line rounded-lg bg-slate-900 px-3 py-2 text-left text-[11px] leading-5 font-normal text-white shadow-xl group-hover:block group-focus-within:block`}>
                          {helpText}
                        </span>
                      </span>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rowsToRender.length > 0 ? rowsToRender.map(({ row, cells, originalIndex }) => (
            <tr key={row?.id || originalIndex} onClick={() => onRowClick && onRowClick(row)} className={`hover:bg-slate-50/50 transition-colors ${onRowClick ? 'cursor-pointer' : ''}`}>
              {cells.map((val, j) => {
                let displayValue = val;
                if (documentsTable && j === 3) {
                  const hasDocumentDate = Boolean(row?.documentDate);
                  const value = row?.documentDate || row?.uploadedDate;
                  displayValue = (
                    <span title={hasDocumentDate ? 'Document Date' : 'Document Date missing; showing upload date as a legacy fallback.'}>
                      {formatDocumentDateAU(value)}
                    </span>
                  );
                }
                return (
                  <td key={j} className={`${rentScheduleTable ? 'px-2 py-3 text-[11px] xl:text-xs leading-4 text-slate-700 whitespace-normal break-words align-top' : propertiesTable ? 'px-2 py-3 text-[10px] 2xl:text-[11px] leading-4 text-slate-700 whitespace-normal break-words align-top' : 'px-6 py-4 text-sm text-slate-700 whitespace-nowrap'} ${stickyLastColumn && j === columns.length - 1 ? 'sticky right-0 z-10 min-w-[170px] bg-white border-l border-slate-100 shadow-[-10px_0_14px_-14px_rgba(15,23,42,0.55)]' : ''}`}>
                    {displayValue}
                  </td>
                );
              })}
            </tr>
          )) : (
            <tr>
              <td colSpan={columns.length} className="px-6 py-12 text-center text-slate-400 text-sm">
                <div className="flex flex-col items-center justify-center gap-2">
                  <SafeIcon icon="Inbox" className="text-3xl text-slate-300" />
                  {emptyMessage}
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {rentScheduleTable && displayedRows.length > 0 && (
        <div
          data-rent-schedules-pagination="true"
          className="sticky bottom-0 z-30 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-white px-3 py-2.5 shadow-[0_-8px_18px_-18px_rgba(15,23,42,0.75)]"
        >
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600">
            <label className="font-medium text-slate-700" htmlFor="rent-schedule-page-size">Rows per page</label>
            <select
              id="rent-schedule-page-size"
              value={rentPageSize}
              onChange={(event) => setRentPageSize(Number(event.target.value))}
              className="min-h-[32px] rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-slate-400">{rentStartRow}-{rentEndRow} of {displayedRows.length}</span>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              onClick={() => goToRentPage(rentPage - 1)}
              disabled={rentPage <= 1}
              className="min-h-[32px] rounded-lg border border-slate-200 bg-white px-3 py-1 font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Previous
            </button>
            <span className="min-w-[78px] text-center font-medium text-slate-600">Page {rentPage} of {rentTotalPages}</span>
            <button
              type="button"
              onClick={() => goToRentPage(rentPage + 1)}
              disabled={rentPage >= rentTotalPages}
              className="min-h-[32px] rounded-lg border border-slate-200 bg-white px-3 py-1 font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const FilterBar = ({ onSearch, onClear, children, hideSearch = false }) => {
  const barRef = useRef(null);
  const uploadPanelRef = useRef(null);
  const [showUploadShortcut, setShowUploadShortcut] = useState(false);
  const isDocumentsFilter = hideSearch && getNodeText(children).includes('Smart Search Documents');
  const isRentSchedulesFilter = !hideSearch && getNodeText(children).includes('Bulk Mark Paid');

  useEffect(() => {
    if (!isDocumentsFilter || !barRef.current) return undefined;

    const bar = barRef.current;
    const chips = bar.previousElementSibling;
    const uploadPanel = chips?.previousElementSibling;
    if (!chips || !uploadPanel) return undefined;

    uploadPanelRef.current = uploadPanel;
    const originalBarStyle = bar.getAttribute('style') || '';
    const originalChipsStyle = chips.getAttribute('style') || '';

    const resetSticky = () => {
      bar.setAttribute('style', originalBarStyle);
      chips.setAttribute('style', originalChipsStyle);
      document.documentElement.style.removeProperty('--documents-table-sticky-top');
    };

    const applySticky = () => {
      const desktop = window.matchMedia('(min-width: 1280px)').matches;
      if (!desktop) {
        resetSticky();
        setShowUploadShortcut(false);
        return;
      }

      const darkTheme = document.documentElement.dataset.mppTheme === 'dark';
      const pageSurface = darkTheme ? 'rgb(8,17,31)' : 'rgb(248,250,252)';
      const cardSurface = darkTheme ? 'rgb(15,23,42)' : 'rgb(255,255,255)';

      chips.style.position = 'sticky';
      chips.style.top = '0px';
      chips.style.zIndex = '35';
      chips.style.background = pageSurface;
      chips.style.backdropFilter = 'none';
      chips.style.paddingTop = '10px';
      chips.style.paddingBottom = '8px';
      chips.style.marginBottom = '0px';
      chips.style.boxShadow = `0 -18px 0 18px ${pageSurface}`;

      const chipsHeight = Math.ceil(chips.getBoundingClientRect().height);
      bar.style.position = 'sticky';
      bar.style.top = `${chipsHeight}px`;
      bar.style.zIndex = '34';
      bar.style.marginBottom = '0px';
      bar.style.background = cardSurface;
      bar.style.backdropFilter = 'none';
      bar.style.boxShadow = darkTheme
        ? '0 8px 18px -16px rgba(0,0,0,0.9)'
        : '0 8px 16px -16px rgba(15,23,42,0.65)';

      const tableTop = chipsHeight + Math.ceil(bar.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--documents-table-sticky-top', `${tableTop}px`);
    };

    applySticky();

    const handleResize = () => applySticky();
    const handleThemeChange = () => applySticky();
    window.addEventListener('resize', handleResize);
    window.addEventListener('mpp:theme-changed', handleThemeChange);
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(applySticky) : null;
    resizeObserver?.observe(chips);
    resizeObserver?.observe(bar);

    const intersectionObserver = typeof IntersectionObserver !== 'undefined'
      ? new IntersectionObserver(([entry]) => {
          const desktop = window.matchMedia('(min-width: 1280px)').matches;
          setShowUploadShortcut(desktop && !entry.isIntersecting);
        }, { threshold: 0.05 })
      : null;
    intersectionObserver?.observe(uploadPanel);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mpp:theme-changed', handleThemeChange);
      resizeObserver?.disconnect();
      intersectionObserver?.disconnect();
      resetSticky();
      uploadPanelRef.current = null;
    };
  }, [isDocumentsFilter]);

  useEffect(() => {
    if (!isRentSchedulesFilter || !barRef.current) return undefined;

    const bar = barRef.current;
    const propertyChips = bar.nextElementSibling;
    const summary = propertyChips?.nextElementSibling;
    if (!propertyChips || !summary) return undefined;
    if (!propertyChips.textContent?.includes('Filter by Property') || !summary.textContent?.includes('Summary for')) return undefined;

    const originalBarStyle = bar.getAttribute('style') || '';
    const originalChipsStyle = propertyChips.getAttribute('style') || '';
    const originalSummaryStyle = summary.getAttribute('style') || '';
    const scrollHost = bar.closest('main');

    const resetSticky = () => {
      bar.setAttribute('style', originalBarStyle);
      propertyChips.setAttribute('style', originalChipsStyle);
      summary.setAttribute('style', originalSummaryStyle);
    };

    const applySticky = () => {
      const desktop = window.matchMedia('(min-width: 1024px)').matches;
      if (!desktop) {
        resetSticky();
        return;
      }

      const darkTheme = document.documentElement.dataset.mppTheme === 'dark';
      const pageSurface = darkTheme ? 'rgb(8,17,31)' : 'rgb(248,250,252)';
      const cardSurface = darkTheme ? 'rgb(15,23,42)' : 'rgb(255,255,255)';

      bar.style.position = 'sticky';
      bar.style.top = '0px';
      bar.style.zIndex = '46';
      bar.style.marginBottom = '0px';
      bar.style.background = cardSurface;
      bar.style.boxShadow = darkTheme
        ? '0 8px 18px -16px rgba(0,0,0,0.9)'
        : '0 8px 16px -16px rgba(15,23,42,0.55)';

      const barHeight = Math.ceil(bar.getBoundingClientRect().height);
      propertyChips.style.position = 'sticky';
      propertyChips.style.top = `${barHeight}px`;
      propertyChips.style.zIndex = '45';
      propertyChips.style.marginBottom = '0px';
      propertyChips.style.paddingTop = '10px';
      propertyChips.style.paddingBottom = '10px';
      propertyChips.style.background = pageSurface;
      propertyChips.style.boxShadow = `0 -14px 0 14px ${pageSurface}`;

      const chipsHeight = Math.ceil(propertyChips.getBoundingClientRect().height);
      summary.style.position = 'sticky';
      summary.style.top = `${barHeight + chipsHeight}px`;
      summary.style.zIndex = '44';
      summary.style.marginBottom = '12px';
      summary.style.background = cardSurface;
      summary.style.boxShadow = darkTheme
        ? '0 10px 22px -18px rgba(0,0,0,0.95)'
        : '0 10px 20px -18px rgba(15,23,42,0.7)';
    };

    const handleWorkspaceWheel = (event) => {
      const desktop = window.matchMedia('(min-width: 1024px)').matches;
      if (!desktop || !scrollHost || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

      const tableScroller = scrollHost.querySelector('[data-rent-schedules-table="true"]');
      if (!tableScroller) return;

      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('[data-rent-schedules-table="true"]')) return;
      if (target?.closest('select, textarea, [role="dialog"]')) return;

      const hostRect = scrollHost.getBoundingClientRect();
      const barRect = bar.getBoundingClientRect();
      const stickyActive = barRect.top <= hostRect.top + 2;
      if (!stickyActive) return;

      const maxScrollTop = Math.max(0, tableScroller.scrollHeight - tableScroller.clientHeight);
      const canScrollDown = event.deltaY > 0 && tableScroller.scrollTop < maxScrollTop - 1;
      const canScrollUp = event.deltaY < 0 && tableScroller.scrollTop > 1;
      if (!canScrollDown && !canScrollUp) return;

      event.preventDefault();
      tableScroller.scrollTop += event.deltaY;
    };

    applySticky();
    const handleResize = () => applySticky();
    const handleThemeChange = () => applySticky();
    window.addEventListener('resize', handleResize);
    window.addEventListener('mpp:theme-changed', handleThemeChange);
    scrollHost?.addEventListener('wheel', handleWorkspaceWheel, { passive: false });
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(applySticky) : null;
    resizeObserver?.observe(bar);
    resizeObserver?.observe(propertyChips);
    resizeObserver?.observe(summary);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('mpp:theme-changed', handleThemeChange);
      scrollHost?.removeEventListener('wheel', handleWorkspaceWheel);
      resizeObserver?.disconnect();
      resetSticky();
    };
  }, [isRentSchedulesFilter]);

  return (
    <div ref={barRef} className="flex flex-wrap items-center gap-3 mb-6 bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
      {!hideSearch && (
        <div className="relative flex-1 min-w-[200px]">
          <SafeIcon icon="Search" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search records..."
            className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
            onChange={(e) => onSearch && onSearch(e.target.value)}
          />
        </div>
      )}
      {children}
      <button onClick={onClear} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors">
        Clear
      </button>
      {isDocumentsFilter && showUploadShortcut && (
        <button
          type="button"
          onClick={() => uploadPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          className="ml-auto hidden xl:inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2 text-sm font-semibold text-amber-700 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
          title="Return to Quick Upload"
          aria-label="Return to Quick Upload"
        >
          <SafeIcon icon="UploadCloud" className="text-base" />
          Upload
        </button>
      )}
    </div>
  );
};

export const FormSection = ({ title, description, children, className = "mb-6", compact = false }) => (
  <div className={`bg-white ${compact ? 'p-4 lg:p-5' : 'p-6'} rounded-xl border border-slate-200 shadow-sm ${className}`}>
    <div className={`${compact ? 'mb-3 pb-3' : 'mb-5 pb-4'} border-b border-slate-100`}>
      <h3 className={`${compact ? 'text-base' : 'text-lg'} font-semibold text-slate-800`}>{title}</h3>
      {description && <p className={`${compact ? 'text-xs mt-0.5' : 'text-sm mt-1'} text-slate-500`}>{description}</p>}
    </div>
    <div className={compact ? 'space-y-3' : 'space-y-4'}>
      {children}
    </div>
  </div>
);

export const EmptyState = ({ icon = "Inbox", title, description, actionLabel, onAction }) => (
  <div className="flex flex-col items-center justify-center p-12 text-center bg-white border border-dashed border-slate-300 rounded-xl">
    <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
      <SafeIcon icon={icon} className="text-2xl text-slate-400" />
    </div>
    <h3 className="text-lg font-semibold text-slate-800 mb-2">{title}</h3>
    <p className="text-sm text-slate-500 max-w-sm mb-6">{description}</p>
    {actionLabel && onAction && (
      <button onClick={onAction} className="px-5 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors">
        {actionLabel}
      </button>
    )}
  </div>
);

export const Badge = ({ label, status = "default" }) => {
  const styles = {
    default: "bg-slate-100 text-slate-600 border-slate-200",
    active: "bg-emerald-100 text-emerald-700 border-emerald-200",
    draft: "bg-slate-100 text-slate-700 border-slate-200",
    review: "bg-indigo-100 text-indigo-700 border-indigo-200",
    overdue: "bg-red-100 text-red-700 border-red-200",
    pending: "bg-amber-100 text-amber-700 border-amber-200",
    archived: "bg-gray-100 text-gray-500 border-gray-200",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${styles[status.toLowerCase()] || styles.default}`}>
      {label}
    </span>
  );
};

export const Tabs = ({ tabs, activeTab, onChange }) => (
  <div className="flex space-x-1 border-b border-slate-200 mb-6 overflow-x-auto">
    {tabs.map(tab => (
      <button
        key={tab.id}
        type="button"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onChange(tab.id);
        }}
        className={`px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
          activeTab === tab.id
            ? 'border-amber-500 text-amber-600'
            : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
        }`}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

export const UploadZone = ({ onFileSelect, label = 'Click to upload or drag and drop', hint = 'PDF, DOCX, ZIP, PNG, JPG or JPEG (max. 15MB)' }) => (
  <div className="relative overflow-hidden border-2 border-dashed border-slate-300 rounded-xl p-10 flex flex-col items-center justify-center bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer group">
    <input type="file" multiple onChange={onFileSelect} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
    <div className="w-12 h-12 bg-white rounded-full shadow-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
      <SafeIcon icon="UploadCloud" className="text-slate-400 text-xl" />
    </div>
    <p className="text-sm font-medium text-slate-700 mb-1">{label}</p>
    <p className="text-xs text-slate-500">{hint}</p>
  </div>
);

export const ChartCard = ({ title }) => (
  <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-col h-72">
    <h3 className="text-sm font-medium text-slate-600 mb-4">{title}</h3>
    <div className="flex-1 flex items-center justify-center bg-slate-50 rounded-lg border border-slate-100">
      <span className="text-sm text-slate-400 flex items-center gap-2">
        <SafeIcon icon="BarChart2" />
        Chart data placeholder
      </span>
    </div>
  </div>
);

export const Modal = ({ isOpen, onClose, title, children, maxWidth = 'max-w-2xl', mobileFullScreen = false }) => {
  if (!isOpen) return null;
  const overlayClass = mobileFullScreen
    ? 'fixed inset-0 z-50 flex items-stretch justify-center bg-slate-900/50 backdrop-blur-sm p-0 sm:items-center sm:p-4 animate-fade-in'
    : 'fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 animate-fade-in';
  const panelClass = mobileFullScreen
    ? `bg-white h-[100dvh] max-h-[100dvh] w-full ${maxWidth} flex flex-col rounded-none shadow-2xl animate-scale-in sm:h-auto sm:max-h-[90vh] sm:rounded-2xl`
    : `bg-white rounded-xl shadow-2xl w-full ${maxWidth} max-h-[90vh] flex flex-col animate-scale-in`;
  return (
    <div className={overlayClass}>
      <div className={panelClass}>
        <div className={`flex shrink-0 items-center justify-between border-b border-slate-200 ${mobileFullScreen ? 'px-4 py-3.5 sm:p-6' : 'p-6'}`}>
          <h3 className={`${mobileFullScreen ? 'text-lg sm:text-xl' : 'text-xl'} min-w-0 break-words font-semibold text-slate-800`}>{title}</h3>
          <button onClick={onClose} className="ml-3 inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500" aria-label="Close">
            <SafeIcon icon="X" className="text-xl" />
          </button>
        </div>
        <div className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${mobileFullScreen ? 'p-4 sm:p-6' : 'p-6'}`}>
          {children}
        </div>
      </div>
    </div>
  );
};